import { createFileRoute } from "@tanstack/react-router";
import type { ToolAvailability } from "@/hooks/types";
import { TOOL_AVAILABILITIES } from "@/utils/availability";
import { DISCOVER_SORTS, type DiscoverSort } from "@/utils/discoverOrder";
import { optionParam, textParam } from "@/utils/searchParams";

// The category drill-down lives in the URL like the source catalogue's
// ?category=…, so browser back/forward walk the filter states. The search term
// joined it for NAV-45: without it, opening a result and coming back lands on
// an empty search box.
//
// The source's `expanded=true` is gone with the fold it opened: a category now
// shows everything it contains at once, because the tools behind that step were
// the ones whose availability a member most needed to see (Jira NAV-122).
//
// `view=all` is the whole catalogue in one list (Jira NAV-144). In the URL for
// the same reasons the category is: back and forward walk in and out of it, and
// "everything the business unit has" is a link somebody can paste into a message. It is
// a *view* rather than a category named "all", because a category with that name
// could exist and this must not collide with it.
//
// `availability` and `sort` narrow and order whichever list is open (Jira
// NAV-28), and are in the URL so that opening a tool and coming back lands on
// the list as it was left. A value no control offers reads as unset.
export type ToolsSearch = {
  category?: string;
  view?: "all";
  q?: string;
  availability?: ToolAvailability;
  sort?: DiscoverSort;
};

export const Route = createFileRoute("/end-user/tools")({
  validateSearch: (search: Record<string, unknown>): ToolsSearch => ({
    category:
      typeof search.category === "string" && search.category
        ? search.category
        : undefined,
    view: search.view === "all" ? "all" : undefined,
    q: textParam(search.q),
    availability: optionParam(TOOL_AVAILABILITIES, search.availability),
    sort: optionParam(DISCOVER_SORTS, search.sort),
  }),
});
